import React, { useReducer } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useInterval } from "usehooks-ts";
import { ReactComponent as CloseIcon } from "../../../../../../assets/close.svg";
import searchAnimation from "./../../../../../../assets/cv-editor/magnifying-glass.json";
import cvAnimation from "./../../../../../../assets/cv-editor/upload.json";
import notesAnimation from "./../../../../../../assets/cv-editor/notes.json";
import finalAnimation from "./../../../../../../assets/cv-editor/typewriter.json";

import micAnimation from "./../../../../../../assets/cv-editor/microphone.json";

// --- API Service & Types ---

// --- Step View Components ---
import { InitialStep } from "./components/InitialStep";
import { LoadingStep } from "./components/LoadingStep";

import {
  CvEditorSession,
  CvEditorSessionStatus,
  ProceedToBasicsResponse,
} from "./types/cv-editor.types";
import {
  archiveSession,
  confirmBasics,
  confirmPositions,
  confirmTargetRole,
  getSessionStatus,
  initiateSession,
  proceedToBasics,
  uploadCv,
} from "../../../../../../services/cv-editor.service";
import { BasicsValidationStep } from "./components/BasicValidationStep";
import { PositionsValidationStep } from "./components/PositionValidationStep";
import { TargetRoleStep } from "./components/TargetRoleStep";
import { InterviewStep } from "./components/interview/InterviewStep";
import { ErrorStep } from "./components/ErrorStep";
import { CompletionStep } from "./components/CompletionStep";
import { ResumeOrStartOverStep } from "./components/ResumeOrStartOverStep";
import { useDelayedMutation } from "./hooks/useDelayedMutation";
import Lottie from "lottie-react";

const getOriginalFileNameFromS3Key = (
  s3Key?: string | null
): string | undefined => {
  if (!s3Key) {
    return undefined;
  }

  const lastSlashIndex = s3Key.lastIndexOf("/");
  const fileNameWithUuid =
    lastSlashIndex >= 0 ? s3Key.substring(lastSlashIndex + 1) : s3Key;

  if (fileNameWithUuid.length > 37) {
    return fileNameWithUuid.substring(37);
  }

  return fileNameWithUuid;
};

// --- State Management (Reducer) ---
interface ModalState {
  session: CvEditorSession;
  isLoading: boolean;
  isPolling: boolean;
  error: string | null;
  hasResumed: boolean;
}

type ModalAction =
  | { type: "SET_SESSION"; payload: CvEditorSession }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_POLLING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "PROCEED_RESPONSE"; payload: ProceedToBasicsResponse }
  | { type: "RESUME_SESSION" };

function modalReducer(state: ModalState, action: ModalAction): ModalState {
  switch (action.type) {
    case "SET_SESSION":
      return {
        ...state,
        session: action.payload,
        isLoading: false,
        isPolling: false,
        error: null,
      };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload, error: null };
    case "SET_POLLING":
      return {
        ...state,
        isPolling: action.payload,
        isLoading: false,
        error: null,
      };
    case "SET_ERROR":
      return {
        ...state,
        error: action.payload,
        isLoading: false,
        isPolling: false,
      };
    case "PROCEED_RESPONSE":
      return {
        ...state,
        session: action.payload.session as CvEditorSession,
        isPolling: !action.payload.isDataReady,
        isLoading: false,
      };
    case "RESUME_SESSION":
      return { ...state, hasResumed: true };
    default:
      return state;
  }
}

// --- Component Definition ---
interface CvEditorModalProps {
  session: CvEditorSession;
  isOpen: boolean;
  onClose: () => void;
}

export const CvEditorModal: React.FC<CvEditorModalProps> = ({
  session: initialSession,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation("cvEditor");
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(modalReducer, {
    session: initialSession,
    isLoading: false,
    isPolling: false,
    error: null,
    hasResumed:
      initialSession.status === CvEditorSessionStatus.AWAITING_CV_ACTION,
  });

  // --- Mutations ---
  const archiveAndRestartMutation = useMutation(
    () => archiveSession(state.session.id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("currentCvSession");
        onClose();
      },
      onError: (error: any) =>
        dispatch({
          type: "SET_ERROR",
          payload: error.message || t("errors.api.archiveFailed"),
        }),
    }
  );

  const startOverMutation = useMutation(
    async () => {
      if (state.session.id) {
        await archiveSession(state.session.id);
      }
      return initiateSession();
    },
    {
      onMutate: () => {
        // Instead of a generic loader, we set a specific status.
        // We can create a temporary session object with this status.
        const restartingSession = {
          ...state.session,
          status: CvEditorSessionStatus.RESTARTING,
        };
        dispatch({
          type: "SET_SESSION",
          payload: restartingSession as CvEditorSession,
        });
      },
      onSuccess: (newSession) => {
        // On success, dispatch the new session from the backend.
        // The status will be AWAITING_CV_ACTION, and the UI will update correctly.
        dispatch({ type: "SET_SESSION", payload: newSession });
        dispatch({ type: "RESUME_SESSION" }); // This will set hasResumed to true
      },
      onError: (error: any) => {
        dispatch({
          type: "SET_ERROR",
          payload: error.message || t("errors.api.startOverFailed"),
        });
      },
    }
  );

  const uploadCvMutation = useMutation(
    (cvFile: File) => uploadCv(state.session.id, cvFile),
    {
      onSuccess: (updatedSession) =>
        dispatch({ type: "SET_SESSION", payload: updatedSession }),
      onError: (error: any) =>
        dispatch({
          type: "SET_ERROR",
          payload: error.message || t("errors.api.uploadFailed"),
        }),
    }
  );

  const proceedWithCvMutation = useDelayedMutation(
    () => proceedToBasics(state.session.id, { fromScratch: false }),
    {
      onMutate: () => dispatch({ type: "SET_LOADING", payload: true }),
      onSettled: (response, error) => {
        if (error) {
          const err = error as any;
          dispatch({
            type: "SET_ERROR",
            payload: err.message || t("errors.api.proceedFailed"),
          });
        } else if (response) {
          dispatch({ type: "PROCEED_RESPONSE", payload: response });
        }
      },
    }
  );

  const proceedWithoutCvMutation = useMutation(
    () => proceedToBasics(state.session.id, { fromScratch: true }),
    {
      onSuccess: (response) => {
        dispatch({ type: "PROCEED_RESPONSE", payload: response });
      },
      onError: (error: any) => {
        dispatch({
          type: "SET_ERROR",
          payload: error.message || t("errors.api.proceedFailed"),
        });
      },
    }
  );

  const confirmBasicsWithCvMutation = useDelayedMutation(
    (basicsData: any) => confirmBasics(state.session.id, basicsData),
    {
      onMutate: () => dispatch({ type: "SET_LOADING", payload: true }),
      onSettled: (response, error) => {
        if (error) {
          dispatch({ type: "SET_ERROR", payload: (error as any).message });
        } else if (response) {
          dispatch({ type: "PROCEED_RESPONSE", payload: response });
        }
      },
    }
  );

  const confirmBasicsWithoutCvMutation = useMutation(
    (basicsData: any) => confirmBasics(state.session.id, basicsData),
    {
      // No onMutate, so no generic loader.
      onSuccess: (response) => {
        // The backend will return isDataReady: true, so PROCEED_RESPONSE works perfectly.
        dispatch({ type: "PROCEED_RESPONSE", payload: response });
      },
      onError: (error: any) => {
        dispatch({ type: "SET_ERROR", payload: error.message });
      },
    }
  );

  const confirmPositionsWithCvMutation = useDelayedMutation(
    (positionsData: any[]) => confirmPositions(state.session.id, positionsData),
    {
      onMutate: () => dispatch({ type: "SET_LOADING", payload: true }),
      onSettled: (updatedSession, error) => {
        if (error) {
          dispatch({ type: "SET_ERROR", payload: (error as any).message });
        } else if (updatedSession) {
          dispatch({ type: "SET_SESSION", payload: updatedSession });
        }
      },
    }
  );

  const confirmPositionsWithoutCvMutation = useMutation(
    (positionsData: any[]) => confirmPositions(state.session.id, positionsData),
    {
      // No onMutate, so no generic loader.
      onSuccess: (updatedSession) => {
        dispatch({ type: "SET_SESSION", payload: updatedSession });
      },
      onError: (error: any) => {
        dispatch({ type: "SET_ERROR", payload: error.message });
      },
    }
  );

  const confirmTargetRoleMutation = useDelayedMutation(
    (targetRole: string) => confirmTargetRole(state.session.id, targetRole),
    {
      onMutate: () => dispatch({ type: "SET_LOADING", payload: true }),
      onSettled: (updatedSession, error) => {
        if (error) {
          const err = error as any;
          dispatch({
            type: "SET_ERROR",
            payload: err.message || t("errors.api.confirmFailed"),
          });
        } else if (updatedSession) {
          // --- START OF THE FIX ---

          // 1. Check the status returned from the backend.
          const shouldPoll =
            updatedSession.status ===
            CvEditorSessionStatus.INTERVIEW_LOADING_POSITIONS;

          // 2. Always update the session with the latest data from the backend.
          // This will set isLoading to false via the reducer.
          dispatch({ type: "SET_SESSION", payload: updatedSession });

          // 3. If the status tells us the data isn't ready, start polling.
          if (shouldPoll) {
            dispatch({ type: "SET_POLLING", payload: true });
          }

          // --- END OF THE FIX ---
        }
      },
    }
  );

  // --- Polling Logic ---
  const handlePolling = async () => {
    if (!state.session || !state.isPolling) return;

    try {
      const latestSession = (await getSessionStatus(
        state.session.id
      )) as CvEditorSession;
      const currentStatus = state.session.status;

      if (currentStatus === CvEditorSessionStatus.INTERVIEW_LOADING_POSITIONS) {
        // The process is "done" when the positionsChatHistory is no longer empty.
        if (
          latestSession.positionsChatHistory &&
          latestSession.positionsChatHistory.length > 0
        ) {
          console.log("Polling success: First position question has arrived.");

          // The job is done. We need to manually change the status to the next step
          // and dispatch the full, new session object.
          latestSession.status =
            CvEditorSessionStatus.INTERVIEW_POSITIONS_IN_PROGRESS;
          dispatch({ type: "SET_SESSION", payload: latestSession });
          return; // We are done with this polling cycle.
        }
      }

      if (latestSession.status !== currentStatus) {
        console.log(
          `Polling: Status changed from ${currentStatus} to ${latestSession.status}. Updating session.`
        );

        dispatch({ type: "SET_SESSION", payload: latestSession });
      }
    } catch (error) {
      console.error("Polling failed:", error);
      dispatch({ type: "SET_ERROR", payload: t("errors.api.pollingFailed") });
    }
  };

  useInterval(handlePolling, state.isPolling ? 2000 : null);

  // --- Render Logic ---
  const renderContent = () => {
    if (state.error) {
      return (
        <ErrorStep
          error={state.error}
          onRetry={() => dispatch({ type: "SET_ERROR", payload: null })}
        />
      );
    }

    // A generic loading state for quick mutations
    if (state.isLoading || state.isPolling) {
      let title = t("loading.processingTitle");
      let steps: { id: string; text: string }[] = [];
      let interval = 1000;
      let animationData: any = null;
      const currentStatus = state.session.status;

      if (currentStatus === CvEditorSessionStatus.CV_PROCESSING) {
        title = t("loading.parsing.title");
        steps = [
          { id: "1", text: t("loading.parsing.basic.step1") },
          { id: "2", text: t("loading.parsing.basic.step2") },
        ];
        animationData = cvAnimation;
        interval = 1000;
      } else if (
        state.session.status ===
          CvEditorSessionStatus.AWAITING_BASICS_VALIDATION ||
        currentStatus === CvEditorSessionStatus.PROCESSING_POSITIONS
      ) {
        title = t("loading.parsing.title");
        steps = [
          { id: "1", text: t("loading.parsing.positions.step1") },
          { id: "2", text: t("loading.parsing.positions.step2") },
        ];
        animationData = searchAnimation;
        interval = 1000;
      } else if (
        state.session.status ===
        CvEditorSessionStatus.AWAITING_POSITIONS_VALIDATION
      ) {
        title = t("loading.parsing.title");
        steps = [
          { id: "1", text: t("loading.parsing.target.step1") },
          { id: "2", text: t("loading.parsing.target.step2") },
        ];
        animationData = searchAnimation;
        interval = 1000;
      } else if (
        state.session.status === CvEditorSessionStatus.AWAITING_TARGET_ROLE ||
        state.session.status ===
          CvEditorSessionStatus.INTERVIEW_LOADING_POSITIONS
      ) {
        title = t("loading.interview.title");
        steps = [
          { id: "1", text: t("loading.interview.step1") },
          { id: "2", text: t("loading.interview.step2") },
        ];
        animationData = notesAnimation;
        interval = 1000;
      } else if (
        state.session.status ===
        CvEditorSessionStatus.INTERVIEW_LOADING_ABOUT_ME
      ) {
        title = t("loading.interview.title");
        steps = [
          { id: "1", text: t("loading.interview-aboutme.step1") },
          { id: "2", text: t("loading.interview-aboutme.step2") },
        ];
        animationData = micAnimation;
        interval = 1000;
      } else if (
        state.session.status === CvEditorSessionStatus.FINAL_GENERATION
      ) {
        title = t("loading.summarizing.title");
        steps = [
          { id: "1", text: t("loading.summarizing.step1") },
          { id: "2", text: t("loading.summarizing.step2") },
          { id: "3", text: t("loading.summarizing.step3") },
        ];
        animationData = finalAnimation;
        interval = 3000;
      } else if (state.session.status === CvEditorSessionStatus.RESTARTING) {
        title = t("loading.restarting.title");
        steps = [
          { id: "1", text: t("loading.restarting.step1") },
          { id: "2", text: t("loading.restarting.step2") },
        ];
        interval = 1000;
      }

      return (
        <LoadingStep
          title={title}
          steps={steps}
          animation={
            <Lottie
              animationData={animationData}
              loop={true}
              style={{ width: "141px", height: "140px" }}
            />
          }
          stepInterval={interval}
        />
      );
    }
    const inProgressStatuses = [
      CvEditorSessionStatus.CV_PROCESSING,
      CvEditorSessionStatus.AWAITING_BASICS_VALIDATION,
      CvEditorSessionStatus.AWAITING_MANUAL_BASICS,
      CvEditorSessionStatus.AWAITING_POSITIONS_VALIDATION,
      CvEditorSessionStatus.AWAITING_MANUAL_POSITIONS,
      CvEditorSessionStatus.AWAITING_TARGET_ROLE,
      CvEditorSessionStatus.INTERVIEW_LOADING_POSITIONS,
      CvEditorSessionStatus.INTERVIEW_LOADING_ABOUT_ME,
      CvEditorSessionStatus.INTERVIEW_POSITIONS_IN_PROGRESS,
      CvEditorSessionStatus.INTERVIEW_ABOUT_ME_IN_PROGRESS,
      CvEditorSessionStatus.FINAL_GENERATION,
    ];

    if (
      inProgressStatuses.includes(state.session.status) &&
      !state.hasResumed
    ) {
      return (
        <ResumeOrStartOverStep
          session={state.session}
          onContinue={() => dispatch({ type: "RESUME_SESSION" })}
          onStartOver={startOverMutation.mutate}
          isLoading={startOverMutation.isLoading}
        />
      );
    }

    // The main router based on session status
    switch (state.session.status) {
      case CvEditorSessionStatus.AWAITING_CV_ACTION:
      case CvEditorSessionStatus.CV_PROCESSING: // This case now only handles the non-polling view
        return (
          <InitialStep
            onUpload={uploadCvMutation.mutate}
            onProceedWithoutCv={proceedWithoutCvMutation.mutate}
            onProceedWithCv={proceedWithCvMutation.mutate}
            isLoading={
              uploadCvMutation.isLoading || proceedWithCvMutation.isLoading
            }
            cvFileName={getOriginalFileNameFromS3Key(state.session.cvS3Key)}
          />
        );

      case CvEditorSessionStatus.AWAITING_BASICS_VALIDATION:
      case CvEditorSessionStatus.AWAITING_MANUAL_BASICS:
        const isManualFlow = !state.session.cvS3Key;
        return (
          <BasicsValidationStep
            session={state.session}
            onConfirm={
              isManualFlow
                ? confirmBasicsWithoutCvMutation.mutate
                : confirmBasicsWithCvMutation.mutate
            }
            isLoading={
              isManualFlow
                ? confirmBasicsWithoutCvMutation.isLoading
                : confirmBasicsWithCvMutation.isLoading
            }
          />
        );

      case CvEditorSessionStatus.AWAITING_POSITIONS_VALIDATION:
      case CvEditorSessionStatus.AWAITING_MANUAL_POSITIONS:
        const isManualFlowForPositions = !state.session.cvS3Key;
        return (
          <PositionsValidationStep
            session={state.session}
            // Pass the correct mutation based on the flow
            onConfirm={
              isManualFlowForPositions
                ? confirmPositionsWithoutCvMutation.mutate
                : confirmPositionsWithCvMutation.mutate
            }
            isLoading={
              isManualFlowForPositions
                ? confirmPositionsWithoutCvMutation.isLoading
                : confirmPositionsWithCvMutation.isLoading
            }
          />
        );

      case CvEditorSessionStatus.AWAITING_TARGET_ROLE:
        return (
          <TargetRoleStep
            onConfirm={confirmTargetRoleMutation.mutate}
            isLoading={confirmTargetRoleMutation.isLoading}
          />
        );

      case CvEditorSessionStatus.INTERVIEW_POSITIONS_IN_PROGRESS:
      case CvEditorSessionStatus.INTERVIEW_ABOUT_ME_IN_PROGRESS:
        return <InterviewStep session={state.session} dispatch={dispatch} />;

      case CvEditorSessionStatus.COMPLETED:
        return <CompletionStep session={state.session} onClose={onClose} />;

      default:
        // Fallback for any other existing status (e.g., user re-opens the modal)
        return (
          <ResumeOrStartOverStep
            session={state.session}
            onContinue={() => dispatch({ type: "RESUME_SESSION" })}
            onStartOver={startOverMutation.mutate}
            isLoading={startOverMutation.isLoading}
          />
        );
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-60 backdrop-blur-sm">
      <div className="flex h-full w-full items-center justify-center p-0 md:p-4">
        <div className="w-full h-full md:max-w-2xl md:max-h-[90vh] md:rounded-lg flex flex-col bg-white shadow-xl">
          <div className="flex-shrink-0 px-6 py-2 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">
              {t("modal.mainTitle")}
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="flex-grow overflow-y-auto">{renderContent()}</div>
        </div>
      </div>
    </div>
  );
};
